<template>
  <div
    class="card"
    v-show="currentCardId == 'mypr'"
    v-loading.fullscreen.lock="enableAi && fullscreenLoading"
  >
    <div class="overflow-scroll-y">
      <div class="card-header">
        <h2>自己PRを入力</h2>
        <p>自己PRを入力してください。</p>
      </div>
      <div class="add-item">
        <el-button
          v-if="enableAi"
          id="prime-reasonai-btn"
          type="success"
          @click="openAiMyprDialog"
          >最新のAIを使う</el-button
        >
        <el-button v-else type="success" @click="exampleVisible = true"
          >例文から追加</el-button
        >
      </div>
      <div class="card-content">
        <NewRirekishoExampleSentenceDialog
          v-if="!enableAi"
          :value="exampleVisible"
          name="mypr-dialog"
          :items="myprExamples"
          show-overwrite-warning
          @input="exampleVisible = $event"
          @select="onSelectExample"
        />
        <template v-if="enableAi">
          <AiMyprDialog
            :visible="aiMyprVisible"
            @update:visible="aiMyprVisible = $event"
          >
            <div class="v--modal-close-top">
              <el-button
                icon="el-icon-close"
                type="text"
                @click="closeAiMyprDialog"
              />
            </div>
            <AiMyprDialogHeader
              catch-text="ヤギプライム限定"
              title="自分らしい文章にしよう"
              lead="あなたにまつわる4つの質問で、より自分らしい文章を作成することができます。"
              attention="※例文選択・AI再生成をすると、現在入力中の文章は上書きされますのでご注意ください。"
            />
            <div v-if="!aiResultView">
              <AiMyprPrQuestionsForm
                :good-mb10="false"
                :good-autosize="true"
                :bad-autosize="true"
                :hobby-autosize="true"
                :wanted-autosize="{ minRows: 3, maxRows: 15 }"
                :appeal-autosize="{ minRows: 3, maxRows: 15 }"
                :good="formatData.ai_mypr_good || ''"
                @update:good="formatData.ai_mypr_good = $event"
                :bad="formatData.ai_mypr_bad || ''"
                @update:bad="formatData.ai_mypr_bad = $event"
                :hobby="formatData.ai_mypr_hobby || ''"
                @update:hobby="formatData.ai_mypr_hobby = $event"
                :wanted="formatData.ai_mypr_wanted || ''"
                @update:wanted="formatData.ai_mypr_wanted = $event"
                :appeal="formatData.ai_mypr_appeal || ''"
                @update:appeal="formatData.ai_mypr_appeal = $event"
              />
              <div class="text-right" style="margin-top: 10px">
                <p v-if="globalVar.isYagiPrime" style="font-size: 0.8rem">
                  <span>1日利用回数：</span><span>5回</span>
                </p>
                <p v-else style="font-size: 0.8rem">
                  <span>利用回数：</span><span>1回</span>
                </p>
                <div class="prime-cv-block">
                  <el-button @click="getAIMypr"
                    >この内容でAI自己PRを作成</el-button
                  >
                </div>
              </div>
            </div>
            <div v-else>
              <ul class="example-sentence">
                <li
                  @click="
                    applyAiResultToMypr()
                    closeAiMyprDialog()
                  "
                >
                  <div class="title">AIからの提案</div>
                  <div class="body" v-html="aiResultMyprResult"></div>
                </li>
              </ul>
              <div
                v-if="!globalVar.isYagiPrime"
                class="text-right"
                style="margin-top: 10px"
              >
                ＼全文を表示する場合は／<br />
                <el-button
                  v-if="inAppPayments"
                  id="prime-reasonai-register-btn"
                  style="
                    color: #fff;
                    background-color: #e3165c;
                    border-color: #e3165c;
                  "
                  @click="goYagiprimeRegister"
                  >ヤギプライム会員になる</el-button
                >
              </div>
            </div>
            <div class="text-right">
              <el-button @click="closeAiMyprDialog">閉じる</el-button>
            </div>
          </AiMyprDialog>
        </template>
        <el-input
          type="textarea"
          v-model="formatData.mypr"
          :autosize="{ minRows: textareaMinRows, maxRows: 15 }"
        />
        <div class="attention-limit character" v-if="previewErrors.mypr">
          <i class="el-icon-warning"></i>
          入力可能な文字数をオーバーしています。文章を短くするか、入力欄の大きい別の履歴書を選んでください。
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import NewRirekishoExampleSentenceDialog from '@/components/format-edit/new-rirekisho/NewRirekishoExampleSentenceDialog.vue'
import AiMyprDialog from '@/components/format-edit/AiMyprDialog.vue'
import AiMyprDialogHeader from '@/components/format-edit/AiMyprDialogHeader.vue'
import AiMyprPrQuestionsForm from '@/components/format-edit/AiMyprPrQuestionsForm.vue'
import { useAiMyprDialogControls } from '@/composables/format-edit/useAiMyprDialogControls'
import { useDesignRirekishoAiMypr } from '@/composables/format-edit/useDesignRirekishoAiMypr'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import {
  useRirekishoFormatData,
  type RirekishoExampleSentence,
  type RirekishoExampleSentences
} from '@/composables/format-edit/useRirekishoFormatData'

const props = withDefaults(
  defineProps<{
    currentCardId?: string
    exampleSentences?: RirekishoExampleSentences
    textareaMinRows: number
    previewErrors: Record<string, string>
    enableAi?: boolean
  }>(),
  {
    enableAi: false
  }
)

const formatData = useRirekishoFormatData()
const { globalVar } = useHostServices()
const exampleVisible = ref(false)
const myprExamples = computed(() => props.exampleSentences?.mypr || [])
const inAppPayments = computed(
  () =>
    !!(globalVar as typeof globalVar & { in_app_payments?: boolean })
      .in_app_payments
)

const { aiMyprVisible, openAiMyprDialog, closeAiMyprDialog } =
  useAiMyprDialogControls()

const {
  fullscreenLoading,
  aiResultView,
  aiResultMyprResult,
  getAIMypr,
  goYagiprimeRegister,
  applyAiResultToMypr
} = useDesignRirekishoAiMypr(formatData)

function onSelectExample(item: RirekishoExampleSentence) {
  formatData.mypr = item.description.replace(/<br>/gi, '\n')
}
</script>
